import React, { useMemo } from "react";

import { Mail, Phone, Calendar, MapPin, LucideIcon } from "lucide-react";

import CommonModal from "../../../Components/Modal";
import { Executive } from "../../../Store/Reducers/OpsSlice";
import { ROLES, isRole } from "../../../Utils/Constants/RoleConstants";
import {
  getTierBadgeClasses,
  useAccountTiers,
} from "../../../Utils/Helper/accountTierHelpers";
import {
  capitalizeFirstLetter,
  formatDateUS,
  getInitials,
} from "../../../Utils/Helper/Helper";

interface ExecutiveAccountsModalProps {
  isOpen: boolean;
  toggle: () => void;
  executive: Executive;
}

// Helper functions
const getLocationString = (executive: Executive): string => {
  const parts = [];
  if (executive.city) parts.push(executive.city);
  if (executive.country) parts.push(executive.country);
  return parts.length > 0 ? parts.join(", ") : executive.region || "N/A";
};

// Reusable components
interface ContactItemProps {
  icon: LucideIcon;
  iconColor?: string;
  value: string;
  breakWords?: boolean;
}

const ContactItem: React.FC<ContactItemProps> = ({
  icon: Icon,
  iconColor = "text-gray-500",
  value,
  breakWords = false,
}) => (
  <div className="flex items-center gap-2">
    <Icon className={`h-4 w-4 ${iconColor}`} />
    <span
      className={`text-sm text-gray-900 ${breakWords ? "break-words" : ""}`}
    >
      {value}
    </span>
  </div>
);

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, children }) => (
  <div>
    <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>
    {children}
  </div>
);

interface MetricItemProps {
  label: string;
  value: string | number;
}

const MetricItem: React.FC<MetricItemProps> = ({ label, value }) => (
  <div className="rounded-lg bg-gray-50 px-3 py-2">
    <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500 mb-0.5">
      {label}
    </div>
    <div className="text-sm font-semibold text-gray-900">{value}</div>
  </div>
);

// Map assignment_type from API to human readable label
const getAssignmentLabel = (assignmentType?: string | null) => {
  if (!assignmentType) return "Sponsor";

  const type = assignmentType.toLowerCase();
  if (type === "primary") return "Primary Sponsor";
  if (type === "secondary") return "Secondary Sponsor";

  return `${assignmentType} Sponsor`;
};

const ExecutiveAccountsModal: React.FC<ExecutiveAccountsModalProps> = ({
  isOpen,
  toggle,
  executive,
}) => {
  const { formatTier } = useAccountTiers();
  const isExecutiveRole = isRole(executive.role, ROLES.EXECUTIVE);

  const locationString = useMemo(
    () => getLocationString(executive),
    [executive.city, executive.country, executive.region]
  );

  const joinDate = useMemo(
    () =>
      executive.joinDate
        ? formatDateUS(executive.joinDate).split(",")[0]
        : "N/A",
    [executive.joinDate]
  );

  const totalAccounts = useMemo(
    () => executive.total_account_count || executive.accounts?.length || 0,
    [executive.total_account_count, executive.accounts?.length]
  );

  const contactItems = useMemo(
    () => [
      { icon: MapPin, iconColor: "text-red-500", value: locationString },
      { icon: Mail, value: executive.email, breakWords: true },
      { icon: Phone, value: executive.phone || "N/A" },
      { icon: Calendar, value: joinDate },
    ],
    [locationString, executive.email, executive.phone, joinDate]
  );

  const sortedAccounts = useMemo(
    () =>
      executive.accounts && executive.accounts.length > 0
        ? [...executive.accounts].sort(
            (a, b) =>
              new Date(b.assigned_on).getTime() -
              new Date(a.assigned_on).getTime()
          )
        : [],
    [executive.accounts]
  );

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={toggle}
      title={`${executive.name} - Details`}
      size="3xl"
    >
      <div className="w-full">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6 border-b border-gray-100 pb-4">
          <div
            className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm ${
              isExecutiveRole ? "bg-sky-500" : "bg-purple-600"
            }`}
          >
            <span className="text-white text-base font-semibold">
              {getInitials(executive.name)}
            </span>
          </div>
          <div className="flex-1">
            <div className="text-lg font-semibold text-gray-900">
              {executive.name}
            </div>
            <div className="mt-0.5 flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-600">
                {executive.title || executive.role}
              </span>
              {executive.experienceLevel && (
                <span className="inline-flex items-center rounded-full bg-purple-50 px-2.5 py-0.5 text-xs font-semibold text-purple-700">
                  {capitalizeFirstLetter(executive.experienceLevel)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Top Two Columns */}
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-5">
              <Section title="Contact Information">
                <div className="space-y-2">
                  {contactItems.map((item, idx) => (
                    <ContactItem key={idx} {...item} />
                  ))}
                </div>
              </Section>

              {executive.languages && executive.languages.length > 0 && (
                <Section title="Languages">
                  <div className="flex flex-wrap gap-2">
                    {executive.languages.map((language, idx) => (
                      <span
                        key={idx}
                        className="text-sm font-medium text-gray-900"
                      >
                        {language}
                      </span>
                    ))}
                  </div>
                </Section>
              )}

              <Section title="Additional Information">
                <div className="flex flex-wrap gap-2">
                  {executive.region && (
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                      {executive.region}
                    </span>
                  )}
                  {executive.experienceLevel && (
                    <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                      {capitalizeFirstLetter(executive.experienceLevel)}
                    </span>
                  )}
                  {executive.grade && (
                    <span className="inline-flex items-center rounded-full bg-purple-50 px-2.5 py-1 text-xs font-medium text-purple-700">
                      Grade {executive.grade}
                    </span>
                  )}
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                      executive.isActive
                        ? "bg-green-50 text-green-700"
                        : "bg-red-50 text-red-600"
                    }`}
                  >
                    {executive.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </Section>
            </div>

            {/* Right Column */}
            <div className="space-y-5">
              <Section title="Performance Metrics">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <MetricItem
                    label="Performance Score"
                    value={
                      isExecutiveRole
                        ? "NA"
                        : executive.performanceScore?.toFixed(1) || "N/A"
                    }
                  />
                  <MetricItem
                    label="Utilization"
                    value={
                      isExecutiveRole
                        ? "NA"
                        : `${executive.utilization?.toFixed(0) || 0}%`
                    }
                  />
                  <MetricItem
                    label="Primary Account Load"
                    value={
                      isExecutiveRole
                        ? "NA"
                        : `${executive.current_accounts || 0} / ${executive.accountLimit || 0}`
                    }
                  />
                </div>
              </Section>

              {!isExecutiveRole && (
                <Section title="Account Summary">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <MetricItem label="Total Accounts" value={totalAccounts} />
                    <MetricItem
                      label="Current Accounts"
                      value={executive.current_accounts || 0}
                    />
                    <MetricItem
                      label="Account Limit"
                      value={executive.accountLimit || "N/A"}
                    />
                  </div>
                </Section>
              )}
            </div>
          </div>

          {/* Full-width Assigned Accounts */}
          {!isExecutiveRole && sortedAccounts.length > 0 && (
            <Section title="Assigned Accounts">
              <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
                {sortedAccounts.map((account, idx) => (
                  <div
                    key={`${account.name}-${idx}`}
                    className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex h-9 w-9 items-center justify-center rounded-md bg-white border border-gray-200 flex-shrink-0">
                        <span className="text-xs font-semibold text-gray-700">
                          {getInitials(account.name)}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {account.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {getAssignmentLabel(account.assignment_type)}
                        </div>
                      </div>
                    </div>
                    <span
                      className={`ml-3 inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs font-semibold ${getTierBadgeClasses(account.tier)}`}
                    >
                      {formatTier(account.tier) ||
                        account.tier?.toUpperCase() ||
                        "N/A"}
                    </span>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      </div>
    </CommonModal>
  );
};

export default ExecutiveAccountsModal;
